/**
 * applySettings — restore persisted appearance preferences from localStorage.
 * Called in main.jsx before ReactDOM.render so settings apply before first paint.
 *
 * Defensive note: localStorage content is treated as untrusted (another
 * script or a tampering user could write anything there). Custom property
 * names and values are validated before they touch the style engine so a
 * corrupted/poisoned cache can't inject arbitrary CSS.
 */

// Only ORIN design-token custom properties may be set from storage.
const SAFE_VAR_NAME = /^--[a-z0-9-]+$/i;
// Colors / simple values only — no url(), quotes, semicolons or braces.
const SAFE_VAR_VALUE = /^[#a-z0-9(),.%\s-]+$/i;

const VALID_TABLE_STYLES = new Set(['orin', 'default', 'spreadsheet', 'compact']);

export const FONT_SIZES = ['small', 'default', 'large'];
export const MOTION_PREFS = ['system', 'reduced'];

// Tailwind sizes here are px-based, so rem scaling has no effect; zoom
// scales the rendered UI uniformly (supported in all evergreen browsers).
const FONT_ZOOM = { small: '0.92', default: '', large: '1.08' };

export function applyFontSize(size) {
  const safe = FONT_SIZES.includes(size) ? size : 'default';
  document.body.style.zoom = FONT_ZOOM[safe];
}

export function applyMotionPref(pref) {
  const safe = MOTION_PREFS.includes(pref) ? pref : 'system';
  if (safe === 'reduced') {
    document.documentElement.setAttribute('data-motion', 'reduced');
  } else {
    document.documentElement.removeAttribute('data-motion');
  }
}

function applyTheme(theme) {
  const root = document.documentElement;
  if (theme === 'dark') {
    root.setAttribute('data-theme', 'dark');
  } else if (theme === 'light') {
    root.removeAttribute('data-theme');
  } else {
    const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
    if (prefersDark) root.setAttribute('data-theme', 'dark');
    else root.removeAttribute('data-theme');
  }
}

function applyAccent(vars) {
  if (!vars || typeof vars !== 'object') return;
  const root = document.documentElement;
  Object.entries(vars).forEach(([k, v]) => {
    if (typeof v !== 'string') return;
    if (!SAFE_VAR_NAME.test(k) || !SAFE_VAR_VALUE.test(v)) return;
    root.style.setProperty(k, v);
  });
}

function applyTableStyle(style) {
  const safe = VALID_TABLE_STYLES.has(style) ? style : 'orin';
  document.documentElement.setAttribute('data-table-style', safe === 'default' ? 'orin' : safe);
}

export function applyStoredSettings() {
  try {
    const theme = localStorage.getItem('orin-theme') || 'system';
    applyTheme(theme);

    const accentRaw = localStorage.getItem('orin-accent');
    if (accentRaw) {
      const accentVars = JSON.parse(accentRaw);
      applyAccent(accentVars);
    }

    const tableStyle = localStorage.getItem('orin-table-style') || 'orin';
    applyTableStyle(tableStyle);

    applyFontSize(localStorage.getItem('orin-font-size') || 'default');
    applyMotionPref(localStorage.getItem('orin-motion') || 'system');
  } catch {
    // ignore storage errors
  }
}
